import { useState, useEffect, useCallback, RefObject } from 'react';
import type { MouseEvent } from 'react';
import useVideoAudioDetection from '@/hooks/useVideoAudioDetection';
import {
  registerUnmutedVideo,
  unregisterUnmutedVideo,
} from '@/utils/media/videoMuteCoordinator';

type UseCardAudioOptions = {
  videoRef: RefObject<HTMLVideoElement>;
  isVideo?: boolean;
  showMuteButton?: boolean;
  shouldLoadVideo: boolean;
  videoSourceId?: string;
};

const useCardAudio = ({
  videoRef,
  isVideo,
  showMuteButton,
  shouldLoadVideo,
  videoSourceId,
}: UseCardAudioOptions) => {
  const [isMuted, setIsMuted] = useState(true);

  const { hasAudio, handleVideoLoadedMetadata } = useVideoAudioDetection({
    videoRef,
    isDetectionEnabled: !!isVideo && !!showMuteButton && shouldLoadVideo,
    videoSourceId,
  });

  const muteThisVideo = useCallback(() => setIsMuted(true), []);

  const handleMuteToggle = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      setIsMuted((prev) => {
        const newMuted = !prev;
        if (!newMuted) registerUnmutedVideo(muteThisVideo);
        else unregisterUnmutedVideo(muteThisVideo);
        return newMuted;
      });
    },
    [muteThisVideo]
  );

  // Sync muted state to video element imperatively
  // (video always renders with `muted` attribute for iOS autoplay compatibility)
  useEffect(() => {
    const video = videoRef.current;
    if (video) video.muted = isMuted;
  }, [isMuted, videoRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => unregisterUnmutedVideo(muteThisVideo);
  }, [muteThisVideo]);

  return {
    isMuted,
    hasAudio,
    handleVideoLoad: handleVideoLoadedMetadata,
    handleMuteToggle,
  };
};

export default useCardAudio;
